import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ProgressPopup } from '@/components/popups/ProgressPopup';
import { RoutePaths } from '@/lib/routing/RoutePaths';

export const GameOrder = [
  RoutePaths.game_lights,
  RoutePaths.game_quizz_01,
  RoutePaths.game_heater,
  RoutePaths.game_quizz_02,
] as const;

export type GamePath = (typeof GameOrder)[number];

export const getGameIndexByPath = (path: string) => {
  return GameOrder.findIndex((gamePath) => path.includes(gamePath));
};

// --------------------- local storage ---------------------

const saveAllGameScoresToLocalStorage = (gameScores: number[]) => {
  try {
    localStorage.setItem(`gameScores`, JSON.stringify(gameScores));
  } catch (error) {
    console.error('Error saving game scores to local storage', error);
  }
};
const getGameScoresFromLocalStorage = () => {
  try {
    const gameScores = localStorage.getItem(`gameScores`);
    if (gameScores) {
      console.log('gameScores', gameScores);
    }
    return gameScores ? JSON.parse(gameScores) : [];
  } catch (error) {
    console.error('Error getting game scores from local storage', error);
    return [];
  }
};

type GameDataContext = {
  currentGameIndex: number;
  totalScore: number;
  nextGamePath: string;
  gameScores: number[];
  currentGameScore: number;
  progressPopupVisible: boolean;
  // methods
  setCurrentGameScore: (score: number) => void;
  setCurrentGameIndex: (index: number) => void;
  openProgressPopup: (onCloseCallback?: () => void | undefined) => void;
  increaseScore: (score: number) => void;
  setGameScore: (gameIndex: number, score: number) => void;
  resetGameScores: () => void;
};

export const gameDataContext = createContext<GameDataContext>({
  currentGameIndex: 0,
  totalScore: 0,
  gameScores: Array(GameOrder.length).fill(0),
  nextGamePath: RoutePaths.intro,
  currentGameScore: 0,
  progressPopupVisible: false,
  // methods
  setCurrentGameIndex: () => {
    console.log('setCurrentGameIndex not implemented');
  },
  setCurrentGameScore: () => {
    console.log('setCurrentGameScore not implemented');
  },
  openProgressPopup: () => {
    console.log('setProgressPopupVisible not implemented');
  },
  increaseScore: () => {
    console.log('increaseScore not implemented');
  },
  setGameScore: () => {
    console.log('setGameScore not implemented');
  },
  resetGameScores: () => {
    console.log('resetGameScores not implemented');
  },
});

// --------------------- provider ------------------------

export const GameDataProvider = ({ children }: { children: ReactNode }) => {
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [progressPopupVisible, setProgressPopupVisible] = useState(false);
  const onProgresCloseCallback = useRef<() => void>();
  const [allGameScores, setAllGameScores] = useState<number[]>(
    Array(GameOrder.length).fill(0)
  );

  const totalScore = allGameScores.reduce((acc, score) => acc + score, 0);
  const currentGameScore = allGameScores[currentGameIndex];
  const nextGamePath: string =
    currentGameIndex < GameOrder.length - 1
      ? GameOrder[currentGameIndex + 1]
      : RoutePaths.thanks; // if we are at the last game, we go to the thanks page

  const openProgressPopup = (onCloseCallback?: () => void | undefined) => {
    setProgressPopupVisible(true);
    onProgresCloseCallback.current = onCloseCallback;
  };

  const closeProgressPopup = () => {
    setProgressPopupVisible(false);
    onProgresCloseCallback.current?.();
  };

  const resetGameScores = () => {
    setAllGameScores(Array(GameOrder.length).fill(0));
    saveAllGameScoresToLocalStorage(Array(GameOrder.length).fill(0));
  };

  /**
   * Increases the score for the current game and updates the total score
   */
  const increaseScore = (score: number) => {
    // Create a copy of the current scores array
    const newGameScores = [...allGameScores];
    // Update the score for the current game
    newGameScores[currentGameIndex] += score;
    // Update the game scores state
    setAllGameScores(newGameScores);
    saveAllGameScoresToLocalStorage(newGameScores);
  };

  /**
   * Sets the score for a specific game
   */
  const setGameScore = (gameIndex: number, score: number) => {
    if (gameIndex >= 0 && gameIndex < allGameScores.length) {
      const newGameScores = [...allGameScores];
      newGameScores[gameIndex] = score;
      setAllGameScores(newGameScores);
      saveAllGameScoresToLocalStorage(newGameScores);
    }
  };

  const setCurrentGameScore = (score: number) => {
    console.log('setCurrentGameScore', { currentGameIndex, score });
    const newGameScores = [
      ...allGameScores.slice(0, currentGameIndex),
      score,
      ...allGameScores.slice(currentGameIndex + 1),
    ];
    setAllGameScores(newGameScores);
    saveAllGameScoresToLocalStorage(newGameScores);
  };

  // on mount, we save the game scores to the local storage
  useEffect(() => {
    const gameScores = getGameScoresFromLocalStorage();
    if (gameScores.length > 0) {
      setAllGameScores(gameScores);
    }
  }, []);

  return (
    <gameDataContext.Provider
      value={{
        currentGameIndex,
        setCurrentGameScore,
        totalScore,
        gameScores: allGameScores,
        currentGameScore,
        progressPopupVisible,
        nextGamePath,
        // methods
        setCurrentGameIndex,
        openProgressPopup,
        increaseScore,
        setGameScore,
        resetGameScores,
      }}
    >
      {children}
      <ProgressPopup
        isOpen={progressPopupVisible}
        onClose={closeProgressPopup}
      />
    </gameDataContext.Provider>
  );
};

// --------------------- hooks ------------------------

export const useGameData = () => {
  return useContext(gameDataContext);
};
